import React, { useEffect, useState } from "react";
import closeIcon from "../../../../../../Assets/images/close-icon.svg";
import ClipLoader from "react-spinners/ClipLoader";
import Loader from "../../../../../../Context/Loader/Loader";
import { showSuccessToast, showErrorToast } from "../../../../../../Context/Toaster/Toaster";
import apiClient from "../../../../../../Config/apiClient";
import {
  FIELD_MAX_LENGTHS,
  VALID_JOB_TYPES,
  VALID_TRUCK_TYPES,
  VALID_PAYMENT_TYPES,
  DATE_TIME_FORMAT_DTP,
} from "../../../Utils/Constants";
import moment from "moment";
import NewLocationAutocomplete from "../../../../../../Components/AutoCompleteLocation/AutoCompleteLocation";
import './AddJobsModal.scss'

const jobsBaseUrl = process.env.REACT_APP_TC_JOBS_BASE_PATH;
const superVisorsUrl = process.env.REACT_APP_TC_SUPERVISORS_BASE_PATH;

const AddJobsModal = ({ jobData, showModal, setRefresh, setShowModal }) => {
  const [formData, setFormData] = useState({
    jobId: "",
    description: "",
    jobType: "",
    truckType: "",
    startLocation: "",
    dumpLocation: "",
    startTime: "",
    endTime: "",
    paymentRate: "",
    paymentType: "",
    trucksRequired: "",
    supervisor: "",
    startLat: "",
    startLng: "",
    endLat: "",
    endLng: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [supervisors, setSupervisors] = useState([]);
  const [prefillLoading] = useState(false);
  const isEdit = jobData && Object.keys(jobData).length > 0;
  const [startLocationMode, setStartLocationMode] = useState("auto");
  const [dumpLocationMode, setDumpLocationMode] = useState("auto");

  const getCurrentDateTimeLocal = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const local = new Date(now.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  };

  const toggleStartLocationMode = () => {
    const newMode = startLocationMode === "auto" ? "manual" : "auto";
    setStartLocationMode(newMode);
    
    if (newMode === "auto" && formData.startLocation) {
      setFormData(prev => ({
        ...prev,
        startLat: "",
        startLng: ""
      }));
    }
    else if (newMode === "manual" && (formData.startLat || formData.startLng)) {
      setFormData(prev => ({
        ...prev,
        startLocation: ""
      }));
    }
  };

  const toggleDumpLocationMode = () => {
    const newMode = dumpLocationMode === "auto" ? "manual" : "auto";
    setDumpLocationMode(newMode);
    
    if (newMode === "auto" && formData.dumpLocation) {
      setFormData(prev => ({
        ...prev,
        endLat: "",
        endLng: ""
      }));
    }
    else if (newMode === "manual" && (formData.endLat || formData.endLng)) {
      setFormData(prev => ({
        ...prev,
        dumpLocation: ""
      }));
    }
  };

  const fetchSupervisors = async () => {
    try {
      const response = await apiClient.post(`${superVisorsUrl}/list`);
      if (response.data.status === "SUCCESS") {
        setSupervisors(response.data.data);
      } else {
        showErrorToast("Failed to load supervisors");
      }
    } catch (err) {
      console.error("Error fetching supervisors:", err);
      showErrorToast("Error fetching supervisors");
    }
  };

  useEffect(() => {
    const now = getCurrentDateTimeLocal();

    const resetForm = () => {
      setFormData({
        jobId: "",
        description: "",
        jobType: "",
        truckType: VALID_TRUCK_TYPES.length === 1 ? VALID_TRUCK_TYPES[0] : (VALID_TRUCK_TYPES.length > 1 ? VALID_TRUCK_TYPES[0] : ""),
        startLocation: "",
        dumpLocation: "",
        startTime: "",
        endTime: "",
        paymentRate: "",
        paymentType: "",
        trucksRequired: "",
        supervisor: "",
        startLat: "",
        startLng: "",
        endLat: "",
        endLng: "",
      });
      setSubmitted(false);
      setFormErrors({});
    };

    const modalEl = document.getElementById("addJob");

    const handleModalClose = () => {
      if (isEdit) {
        const now = getCurrentDateTimeLocal();
        setFormData({
          jobId: jobData.job_name || "",
          description: jobData.job_description || "",
          jobType: jobData.job_type || "",
          truckType: jobData.truck_type || "",
          startLocation: jobData.start_location || "",
          dumpLocation: jobData.end_location || "",
          startTime: jobData.start_date_time
            ? moment.utc(jobData.start_date_time).local().format(DATE_TIME_FORMAT_DTP)
            : now,
          endTime: jobData.end_date_time
            ? moment.utc(jobData.end_date_time).local().format(DATE_TIME_FORMAT_DTP)
            : now,
          paymentRate: jobData.payment_rate?.toString() || "",
          paymentType: jobData.payment_type || "",
          trucksRequired: jobData.num_trucks_required?.toString() || "",
          supervisor: jobData.supervisor_id || "",
          startLat: jobData.start_location_latitude || "",
          startLng: jobData.start_location_longitude || "",
          endLat: jobData.end_location_latitude || "",
          endLng: jobData.end_location_longitude || "",
        });
      }
      else {
        resetForm();
      }

      setSubmitted(false);
      setFormErrors({});
    };

    if (modalEl) {
      modalEl.addEventListener("hidden.bs.modal", handleModalClose);
    }

    if (showModal) {
      fetchSupervisors();
      if (isEdit) {
        const initialStartMode = jobData.start_location ? "auto" : "manual";
        const initialDumpMode = jobData.end_location ? "auto" : "manual";
        
        setStartLocationMode(initialStartMode);
        setDumpLocationMode(initialDumpMode);

        setFormData({
          jobId: jobData.job_name || "",
          description: jobData.job_description || "",
          jobType: jobData.job_type || "",
          truckType: VALID_TRUCK_TYPES.length === 1 ? VALID_TRUCK_TYPES[0] : (jobData.truck_type || ""),
          startLocation: jobData.start_location || "",
          dumpLocation: jobData.end_location || "",
          startLat: jobData.start_location_latitude || "",
          startLng: jobData.start_location_longitude || "",
          endLat: jobData.end_location_latitude || "",
          endLng: jobData.end_location_longitude || "",
          startTime: jobData.start_date_time
            ? moment.utc(jobData.start_date_time).local().format(DATE_TIME_FORMAT_DTP)
            : now,
          endTime: jobData.end_date_time
            ? moment.utc(jobData.end_date_time).local().format(DATE_TIME_FORMAT_DTP)
            : now,
          paymentRate: jobData.payment_rate?.toString() || "",
          paymentType: jobData.payment_type || "",
          trucksRequired: jobData.num_trucks_required?.toString() || "",
          supervisor: jobData.supervisor_id || "",
        });
      } else {
        resetForm();
      }
    }

    return () => {
      if (modalEl) {
        modalEl.removeEventListener("hidden.bs.modal", handleModalClose);
      }
    };
  }, [showModal, isEdit, jobData]);

  const isInProgressEditMode = isEdit && jobData.job_status === "In Progress";

  const handleChange = (e) => {
    const { name, value } = e.target;
    const limit = FIELD_MAX_LENGTHS[name];
    if (limit && value.length > limit) return;

    let newValue = value;

    if (name === "paymentRate") {
      if (!/^\d*\.?\d*$/.test(value)) return;
    }

    if (name === "trucksRequired") {
      if (!/^\d*$/.test(value)) return;
    }



    setFormData((prev) => ({ ...prev, [name]: newValue }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const getFieldError = (name, value) => {
    const fieldNames = {
      description: "Description",
      jobType: "Job Type",
      truckType: "Truck Type",
      startLocation: "Start Location",
      dumpLocation: "Dump Location",
      startTime: "Start Time",
      endTime: "End Time",
      paymentRate: "Payment Rate",
      paymentType: "Payment Type",
      trucksRequired: "Trucks Required",
      startLat: "Start Latitude",
      startLng: "Start Longitude",
      endLat: "End Latitude",
      endLng: "End Longitude",
    };

    const fieldLabel = fieldNames[name] || name;

    if (name === "startLocation" && startLocationMode === "manual") return "";
    if (name === "dumpLocation" && dumpLocationMode === "manual") return "";

    if ((name === "startLat" || name === "startLng") && startLocationMode === "auto") return "";
    if ((name === "endLat" || name === "endLng") && dumpLocationMode === "auto") return "";

    if (!value || String(value).trim() === "") {
      return `${fieldLabel} is required.`;
    }

    if (name === "startTime" && !isEdit) {
      const now = new Date();
      const start = new Date(value);
      if (start < now) {
        return `${fieldLabel} & Date must be in the future.`;
      }
    }

    if (name === "endTime") {
      const start = new Date(formData.startTime);
      const end = new Date(value);
      if (value && formData.startTime && end <= start) {
        return `${fieldLabel} & Date must be after Start Time.`;
      }
    }

    if (name === "paymentRate" && isNaN(value)) {
      return `${fieldLabel} must be a valid number.`;
    }

    if (name === "trucksRequired" && (!/^\d+$/.test(value) || parseInt(value, 10) < 1)) {
      return `${fieldLabel} must be a valid count.`;
    }

    return "";
  };

  const validateForm = () => {
    const errors = {};
    
    // Start Location Validation
    if (startLocationMode === "manual") {
      if (!formData.startLat?.trim()) {
        errors.startLat = "Latitude is required";
      }
      if (!formData.startLng?.trim()) {
        errors.startLng = "Longitude is required";
      }
    } else {
      if (!formData.startLocation?.trim()) {
        errors.startLocation = "Start Location is required";
      }
    }

    // Dump Location Validation
    if (dumpLocationMode === "manual") {
      if (!formData.endLat?.trim()) {
        errors.endLat = "Latitude is required";
      }
      if (!formData.endLng?.trim()) {
        errors.endLng = "Longitude is required";
      }
    } else {
      if (!formData.dumpLocation?.trim()) {
        errors.dumpLocation = "Dump Location is required";
      }
    }

    // Other field validations
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "supervisor" || key === "jobId" || 
          key === "startLat" || key === "startLng" || 
          key === "endLat" || key === "endLng" || 
          key === "startLocation" || key === "dumpLocation") return;
      
      const error = getFieldError(key, value);
      if (error) errors[key] = error;
    });

    if (new Date(formData.startTime) > new Date(formData.endTime)) {
      errors.startTime = "Start time must be before end time.";
    }

    setFormErrors(errors);
    setSubmitted(true);
    return Object.keys(errors).length === 0;
  };

  const showError = (field) => submitted && formErrors[field];

  const parseOrZero = (val) => {
    return val !== undefined && val !== null && val !== "" && !isNaN(parseFloat(val))
      ? parseFloat(val)
      : 0;
  };

  const handleSubmit = async () => {
    setSubmitted(true);

    if (!validateForm()) return;

    const parsedEndLat = parseOrZero(formData.endLat);
    const parsedEndLng = parseOrZero(formData.endLng);

    const payload = {
      job_description: formData.description.trim(),
      job_type: formData.jobType,
      truck_type: formData.truckType,
      start_location_latitude: parseOrZero(formData.startLat),
      start_location_longitude: parseOrZero(formData.startLng),
      start_location: formData.startLocation,
      end_location: formData.dumpLocation,
      payment_rate: formData.paymentRate.trim(),
      payment_type: formData.paymentType,
      end_location_latitude: parsedEndLat,
      end_location_longitude: parsedEndLng,
      num_trucks_required: parseInt(formData.trucksRequired, 10),
      supervisor_id:
        formData.supervisor === "Select" || formData.supervisor === ""
          ? null
          : formData.supervisor,

    };

    const originalEndLat = parseFloat(jobData?.end_location_latitude || 0);
    const originalEndLng = parseFloat(jobData?.end_location_longitude || 0);

    if (
      !isEdit ||
      parsedEndLat !== originalEndLat ||
      parsedEndLng !== originalEndLng
    ) {
      payload.end_location_latitude = parsedEndLat;
      payload.end_location_longitude = parsedEndLng;
    }

    if (
      !isEdit ||
      formData.startTime !== moment.utc(jobData.start_date_time).local().format(DATE_TIME_FORMAT_DTP)
    ) {
      payload.start_date_time = moment(formData.startTime).utc().toISOString();
    }

    if (
      !isEdit ||
      formData.endTime !== moment.utc(jobData.end_date_time).local().format(DATE_TIME_FORMAT_DTP)
    ) {
      payload.end_date_time = moment(formData.endTime).utc().toISOString();
    }

    try {
      setLoading(true);

      delete payload.job_name;

      if (isEdit) {
        await apiClient.put(`${jobsBaseUrl}/edit/${jobData.id}`, payload);
        showSuccessToast("Job updated successfully!");
      } else {
        await apiClient.post(`${jobsBaseUrl}/add-jobs`, payload);
        showSuccessToast("Job added successfully!");
      }

      setRefresh((prev) => !prev);

      if (typeof setShowModal === "function") {
        setShowModal(false);
      }
    } catch (err) {
      console.error("API Error: ", err.response?.data);
      showErrorToast(err?.response?.data?.error_data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className={`modal fade ${showModal ? "show d-block" : ""}`} tabIndex="-1" role="dialog" aria-labelledby="addJobLabel" aria-hidden={!showModal}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="ajob-content">
            <div className="cpf-top">
              <h2>{isEdit ? "Edit Job" : "Add Job"}</h2>
              <button className="m-close" aria-label="Close" onClick={() => setShowModal(false)} id="addJobCloseBtn" >
                <img src={closeIcon} alt="Close" />
              </button>
            </div>
            <div className="cprofile-inner">
              {prefillLoading ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <ClipLoader size={40} color="grey" />
                </div>
              ) : (
                <div className="cp-form">
                  <div className="row">
                    {/* Job ID */}
                    {isEdit ? (
                      <div className="col-md-12">
                        <div className="cp-split">
                          <label className="fi-label">Job ID <span>*</span></label>
                          <input
                            type="text"
                            name="jobId"
                            className={`common-input ${showError("jobId") ? "error" : ""}`}
                            value={formData.jobId}
                            onChange={handleChange}
                            maxLength={FIELD_MAX_LENGTHS.jobId}
                            disabled={true}
                          />
                          {showError("jobId") && <span className="error-text active">{formErrors.jobId}</span>}
                        </div>
                      </div>
                    ) : (<></>)}

                    {/* Description */}
                    <div className="col-md-12">
                      <div className="cp-split">
                        <label className="fi-label">Description <span>*</span></label>
                        <textarea
                          name="description"
                          className={`common-text ${showError("description") ? "error" : ""}`}
                          value={formData.description}
                          onChange={handleChange}
                          maxLength={FIELD_MAX_LENGTHS.description}
                          placeholder="Describe the job type and mention if the truck needs anything special like chute or pins."
                        ></textarea>
                        {showError("description") && <span className="error-text active">{formErrors.description}</span>}
                      </div>
                    </div>

                    {/* Job Type */}
                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">Job Type <span>*</span></label>
                        <select
                          name="jobType"
                          className={`common-select ${showError("jobType") ? "error" : ""}`}
                          value={formData.jobType}
                          onChange={handleChange}
                          disabled={isInProgressEditMode}
                        >
                          <option value="">--Select--</option>
                          {VALID_JOB_TYPES.map((type) => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                        {showError("jobType") && <span className="error-text active">{formErrors.jobType}</span>}
                      </div>
                    </div>

                    {/* Truck Type */}

                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">Truck Type <span>*</span></label>
                        {VALID_TRUCK_TYPES.length === 1 ? (
                          <select
                            name="truckType"
                            className="common-select"
                            value={VALID_TRUCK_TYPES[0]}
                            disabled
                          >
                            <option value={VALID_TRUCK_TYPES[0]}>{VALID_TRUCK_TYPES[0]}</option>
                          </select>
                        ) : (
                          <select
                            name="truckType"
                            className={`common-select ${showError("truckType") ? "error" : ""}`}
                            value={formData.truckType}
                            onChange={handleChange}
                            disabled={isInProgressEditMode}
                          >
                            <option value="">--Select--</option>
                            {VALID_TRUCK_TYPES.map((type) => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                        )}
                        {showError("truckType") && <span className="error-text active">{formErrors.truckType}</span>}
                      </div>
                    </div>


                    {/* Start Location */}
                    <div className="col-md-6">
                      <div className="cp-split">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <label className="fi-label">Start Location <span>*</span></label>
                          <div className="location-toggle">
                              <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="startLocationToggle"
                              checked={startLocationMode === "manual"}
                              onChange={toggleStartLocationMode}
                            />
                            <label className="form-check-label label-toggle" htmlFor="startLocationToggle">
                              {startLocationMode === "manual" ? "Manual" : "Auto"}
                            </label>
                          </div>
                          </div>
                        </div>

                        {startLocationMode === "auto" ? (
                          <>
                            <NewLocationAutocomplete
                              key={`start-${showModal}-${formData.startLocation}`}
                              value={formData.startLocation}
                              required={true}
                              disabled={isInProgressEditMode}
                              onPlaceSelected={({ label, lat, lng }) => {
                                setFormData((prev) => ({
                                  ...prev,
                                  startLocation: label,
                                  startLat: lat,
                                  startLng: lng,
                                }));
                                setFormErrors((prev) => ({ ...prev, startLocation: "" }));
                              }}
                            />
                            {showError("startLocation") && (
                              <span className="error-text active">{formErrors.startLocation}</span>
                            )}
                          </>
                        ) : (
                          <div className="row">
                            <div className="col-6">
                              <div className="input-wrapper">
                                <input
                                  type="text"
                                  name="startLat"
                                  className={`common-input ${showError("startLat") ? "error" : ""}`}
                                  placeholder="Lat"
                                  value={formData.startLat}
                                  onChange={handleChange}
                                />
                                {showError("startLat") && (
                                  <div className="error-text active">{formErrors.startLat}</div>
                                )}
                              </div>
                            </div>
                            <div className="col-6">
                              <div className="input-wrapper"style={{position:'relative'}}>
                                <input
                                  type="text"
                                  name="startLng"
                                  className={`common-input ${showError("startLng") ? "error" : ""}`}
                                  placeholder="Long"
                                  value={formData.startLng}
                                  onChange={handleChange}
                                />
                                {showError("startLng") && (
                                  <div className="error-text active">{formErrors.startLng}</div>
                                )}
                              </div>
                            </div>
                          </div>

                        )}
                      </div>
                    </div>

                    {/* Dump Location */}
                    <div className="col-md-6">
                      <div className="cp-split">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <label className="fi-label">Dump Location <span>*</span></label>
                           <div className="location-toggle">
                            <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="dumpLocationToggle"
                              checked={dumpLocationMode === "manual"}
                              onChange={toggleDumpLocationMode}
                            />
                            <label className="form-check-label label-toggle" htmlFor="dumpLocationToggle">
                              {dumpLocationMode === "manual" ? "Manual" : "Auto"}
                            </label>
                          </div>
                           </div>
                        </div>

                        {dumpLocationMode === "auto" ? (
                          <>
                            <NewLocationAutocomplete
                              key={`dump-${showModal}-${formData.dumpLocation}`}
                              value={formData.dumpLocation}
                              required={true}
                              disabled={isInProgressEditMode}
                              onPlaceSelected={({ label, lat, lng }) => {
                                setFormData((prev) => ({
                                  ...prev,
                                  dumpLocation: label,
                                  endLat: lat,
                                  endLng: lng,
                                }));
                                setFormErrors((prev) => ({ ...prev, dumpLocation: "" }));
                              }}
                            />
                            {showError("dumpLocation") && (
                              <span className="error-text active">{formErrors.dumpLocation}</span>
                            )}
                          </>
                        ) : (
                          <div className="row">
                            <div className="col-6">
                              <div className="input-wrapper">
                                <input
                                  type="text"
                                  name="endLat"
                                  className={`common-input ${showError("endLat") ? "error" : ""}`}
                                  placeholder="Lat"
                                  value={formData.endLat}
                                  onChange={handleChange}
                                />
                                {showError("endLat") && (
                                  <div className="error-text active">{formErrors.endLat}</div>
                                )}
                              </div>
                            </div>

                            <div className="col-6">
                              <div className="input-wrapper"style={{position:'relative'}}>
                                <input
                                  type="text"
                                  name="endLng"
                                  className={`common-input ${showError("endLng") ? "error" : ""}`}
                                  placeholder="Long"
                                  value={formData.endLng}
                                  onChange={handleChange}
                                />
                                {showError("endLng") && (
                                  <div className="error-text active">{formErrors.endLng}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Start Time */}
                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">Start Time <span>*</span></label>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <input
                            type="date"
                            className={`common-input ${showError("startTime") ? "error" : ""}`}
                            style={{ flex: '3' }}
                            value={formData.startTime ? formData.startTime.slice(0, 10) : ''}
                            min={!isEdit ? new Date().toISOString().slice(0, 10) : undefined}
                            onChange={(e) => {
                              const newDate = e.target.value;
                              const currTime = formData.startTime ? formData.startTime.slice(11, 16) : '';
                              let t = currTime;
                              if (!isEdit && newDate && t) {
                                const now = new Date();
                                const td = now.toISOString().slice(0, 10);
                                if (newDate === td && new Date(`${newDate}T${t}`) < now) {
                                  t = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
                                }
                              }
                              setFormData(prev => ({ ...prev, startTime: newDate ? `${newDate}T${t}` : '' }));
                              setFormErrors(prev => ({ ...prev, startTime: '' }));
                            }}
                            disabled={isInProgressEditMode}
                          />
                          <input
                            type="time"
                            className={`common-input ${showError("startTime") ? "error" : ""}`}
                            style={{ flex: '2' }}
                            value={formData.startTime ? formData.startTime.slice(11, 16) : ''}
                            min={(() => {
                              if (isEdit) return undefined;
                              const now = new Date();
                              const todayStr = now.toISOString().slice(0, 10);
                              if ((formData.startTime ? formData.startTime.slice(0, 10) : '') === todayStr) {
                                return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
                              }
                              return undefined;
                            })()}
                            onChange={(e) => {
                              const newTime = e.target.value;
                              const dateStr = formData.startTime ? formData.startTime.slice(0, 10) : new Date().toISOString().slice(0, 10);
                              setFormData(prev => ({ ...prev, startTime: `${dateStr}T${newTime}` }));
                              setFormErrors(prev => ({ ...prev, startTime: '' }));
                            }}
                            disabled={isInProgressEditMode}
                          />
                        </div>
                        {showError("startTime") && <span className="error-text active">{formErrors.startTime}</span>}
                      </div>
                    </div>

                    {/* End Time */}
                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">End Time <span>*</span></label>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <input
                            type="date"
                            className={`common-input ${showError("endTime") ? "error" : ""}`}
                            style={{ flex: '3' }}
                            value={formData.endTime ? formData.endTime.slice(0, 10) : ''}
                            min={formData.startTime ? formData.startTime.slice(0, 10) : (!isEdit ? new Date().toISOString().slice(0, 10) : undefined)}
                            onChange={(e) => {
                              const newDate = e.target.value;
                              const currTime = formData.endTime ? formData.endTime.slice(11, 16) : '';
                              setFormData(prev => ({ ...prev, endTime: newDate ? `${newDate}T${currTime}` : '' }));
                              setFormErrors(prev => ({ ...prev, endTime: '' }));
                            }}
                            disabled={isInProgressEditMode}
                          />
                          <input
                            type="time"
                            className={`common-input ${showError("endTime") ? "error" : ""}`}
                            style={{ flex: '2' }}
                            value={formData.endTime ? formData.endTime.slice(11, 16) : ''}
                            min={(() => {
                              const startDateStr = formData.startTime ? formData.startTime.slice(0, 10) : '';
                              const endDateStr = formData.endTime ? formData.endTime.slice(0, 10) : '';
                              if (startDateStr && endDateStr === startDateStr) {
                                return formData.startTime ? formData.startTime.slice(11, 16) : undefined;
                              }
                              return undefined;
                            })()}
                            onChange={(e) => {
                              const newTime = e.target.value;
                              const dateStr = formData.endTime ? formData.endTime.slice(0, 10) : (formData.startTime ? formData.startTime.slice(0, 10) : new Date().toISOString().slice(0, 10));
                              setFormData(prev => ({ ...prev, endTime: `${dateStr}T${newTime}` }));
                              setFormErrors(prev => ({ ...prev, endTime: '' }));
                            }}
                            disabled={isInProgressEditMode}
                          />
                        </div>
                        {showError("endTime") && <span className="error-text active">{formErrors.endTime}</span>}
                      </div>
                    </div>

                    {/* Payment Rate */}
                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">Payment Rate (CAD) <span>*</span></label>
                        <input
                          type="text"
                          name="paymentRate"
                          className={`common-input ${showError("paymentRate") ? "error" : ""}`}
                          value={formData.paymentRate}
                          onChange={handleChange}
                          maxLength={FIELD_MAX_LENGTHS.paymentRate}
                          disabled={isInProgressEditMode}
                        />
                        {showError("paymentRate") && <span className="error-text active">{formErrors.paymentRate}</span>}
                      </div>
                    </div>

                    {/* Payment Type */}
                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">Payment Type <span>*</span></label>
                        <select
                          name="paymentType"
                          className={`common-select ${showError("paymentType") ? "error" : ""}`}
                          value={formData.paymentType}
                          onChange={handleChange}
                          disabled={isInProgressEditMode}
                        >
                          <option value="">--Select--</option>
                          {VALID_PAYMENT_TYPES.map((type) => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                        {showError("paymentType") && <span className="error-text active">{formErrors.paymentType}</span>}
                      </div>
                    </div>

                    {/* Trucks Required */}
                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">Trucks Required <span>*</span></label>
                        <input
                          type="text"
                          name="trucksRequired"
                          className={`common-input ${showError("trucksRequired") ? "error" : ""}`}
                          value={formData.trucksRequired}
                          onChange={handleChange}
                          maxLength={FIELD_MAX_LENGTHS.trucksRequired}
                          disabled={isInProgressEditMode}
                        />
                        {showError("trucksRequired") && <span className="error-text active">{formErrors.trucksRequired}</span>}
                      </div>
                    </div>

                    {/* Supervisor */}
                    <div className="col-md-6">
                      <div className="cp-split">
                        <label className="fi-label">Supervisor</label>
                        <select
                          name="supervisor"
                          className="common-select"
                          value={formData.supervisor}
                          onChange={handleChange}
                        >
                          <option value="">--Select--</option>
                          {supervisors.map((sup) => (
                            <option key={sup.id} value={sup.id}>
                              {sup.first_name} {sup.last_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="m-button" style={{ right: '25px', bottom: '25px', margin: '0' }}>
                    <button type="button" className="cp-button save-btn" onClick={handleSubmit} disabled={loading}>
                      {loading ? <Loader /> : 'Save'}
                    </button>
                  </div>

                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddJobsModal;